/**
 * O protocolo de assinatura pelo painel do dado (E203.F2, ADR-0146 §4): o formulário de sempre
 * (`ProtocoloForm`, as oito seções), com o protocolo gravado; salvar grava o protocolo do caso (que
 * reconfere as peças) e a mesa relê o documento. Com peça concluída, o protocolo não muda (409): o
 * painel diz o caminho, reabrir a conclusão. O instante da assinatura muda por aqui até ganhar o
 * campo próprio.
 *
 * E203.F4.C (F-E203.F4-07): com fundamento obrigatório ("Salvar" espera por ele), pela rota do
 * painel (`PUT /casos/{id}/dados/protocolo`), que grava o antes e o depois no histórico do caso.
 */
import { useState } from 'react';

import { ProtocoloForm } from '@/components/ExameEletronico/ProtocoloForm';
import { ptBR } from '@/i18n/pt-BR';
import { exigeConclusao, pecaConcluida } from '@/lib/exame_eletronico_ccb';
import { ExameEletronicoError } from '@/lib/exame_eletronico_erros';
import { protocoloDoCaso, type ProtocoloAssinaturaData } from '@/lib/exame_eletronico_protocolo';
import { salvarProtocoloPeloPainel } from '@/lib/revisaoDoDado';

import { Fundamento, fundamentoValido } from './Fundamento';
import { BotaoSalvar, useGravacao } from './Gravacao';
import type { PropsDoConteudo } from './tipos';

const t = ptBR.painelDoDado.protocolo;

function comPecaConcluida(err: unknown): boolean {
  return (
    err instanceof ExameEletronicoError &&
    (err.reason === 'concluido' || err.reason === 'protocolo_peca_concluida')
  );
}

export function ProtocoloNoPainel(props: PropsDoConteudo) {
  const { client, caseId, pedido, mesa, acoes, onMudou } = props;
  const concluido = (mesa.ccbs ?? []).some((p) => exigeConclusao(p) && pecaConcluida(p));
  const [fundamento, setFundamento] = useState('');
  const gravacao = useGravacao<ProtocoloAssinaturaData>(
    async (protocolo) => {
      try {
        const doCaso = protocoloDoCaso(protocolo);
        await salvarProtocoloPeloPainel(client, caseId, doCaso, fundamento.trim());
      } catch (err) {
        if (comPecaConcluida(err)) return t.concluido;
        throw err;
      }
      return null;
    },
    () => {
      acoes.salvo();
      onMudou();
    },
  );
  return (
    <div className="space-y-sc-3" data-testid="painel-protocolo">
      <p className="text-body-sm text-fg-muted">
        {pedido.origem.chave === 'instante' ? t.instante : t.texto}
      </p>
      {concluido ? (
        <p className="text-body-sm text-warning-700" data-testid="painel-protocolo-concluido">
          {t.concluido}
        </p>
      ) : null}
      <Fundamento id="painel-protocolo-fundamento" valor={fundamento} onChange={setFundamento} />
      <ProtocoloForm
        idPrefix="painel-protocolo"
        protocoloInicial={mesa.protocolo}
        acaoEmCurso={gravacao.estado === 'salvando' ? 'salvando' : null}
        concluido={concluido}
        ocultarCodigoDeclarado
        mostrarAcoesDeVerificacao={false}
        salvarDesligado={!fundamentoValido(fundamento)}
        onSalvar={(protocolo) => void gravacao.salvar(protocolo)}
        onVerificar={() => undefined}
        onAtualizarConsultas={() => undefined}
      />
      {gravacao.estado === 'salvo' || gravacao.estado === 'falhou' ? (
        <BotaoSalvar
          estado={gravacao.estado}
          recusa={gravacao.recusa}
          desligado
          onSalvar={() => undefined}
          testId="painel-protocolo-estado"
        />
      ) : null}
    </div>
  );
}
